import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Lightbulb, FileText, Search, Shield, Users, Truck, HardHat,
  MessageSquare, Brain, Target, ChevronDown, Sparkles, Copy,
  CheckCircle2, Wrench, ClipboardList, BarChart3, BookOpen
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

/**
 * AIToolsConsole - Centralized AI tools access for Zodiac ProLab
 * Provides categorized AI assistants and prompt tools
 * Designed to be expandable for future tools
 */

// Tool categories and their tools
const AI_TOOLS = {
  business: {
    name: "Business & Enterprise",
    color: "#1E3A8A",
    tools: [
      {
        id: "idea-generator",
        icon: Lightbulb,
        name: "Business Idea Generator",
        description: "Generate innovative business ideas based on market trends and opportunities.",
        promptTemplate: "Generate 5 innovative business ideas for [INDUSTRY] that leverage AI technology. Consider market trends, customer pain points, and scalability.",
        category: "business"
      },
      {
        id: "project-charter",
        icon: Target,
        name: "Project Charter Builder",
        description: "Create comprehensive project charters with objectives, scope, and milestones.",
        promptTemplate: "Create a project charter for [PROJECT NAME]. Include: 1) Project objectives, 2) Scope and boundaries, 3) Key stakeholders, 4) Success criteria, 5) Timeline milestones, 6) Risk considerations.",
        category: "business"
      },
      {
        id: "business-plan",
        icon: FileText,
        name: "Business Plan Assistant",
        description: "Draft business plan sections with market analysis and financial projections.",
        promptTemplate: "Help me draft the [SECTION] section of a business plan for [BUSINESS TYPE]. Include industry-standard components and best practices.",
        category: "business"
      }
    ]
  },
  leadership: {
    name: "Leadership & Communication",
    color: "#2563EB",
    tools: [
      {
        id: "feedback-assistant",
        icon: Users,
        name: "Leadership Feedback Assistant",
        description: "Generate constructive feedback for team performance reviews.",
        promptTemplate: "Help me provide constructive feedback to [ROLE] about [SITUATION]. The feedback should be specific, actionable, and growth-oriented.",
        category: "leadership"
      },
      {
        id: "communication-coach",
        icon: MessageSquare,
        name: "Communication Improvement Assistant",
        description: "Improve professional communication and messaging effectiveness.",
        promptTemplate: "Review and improve this [MESSAGE TYPE] for clarity, professionalism, and impact: [YOUR MESSAGE]",
        category: "leadership"
      }
    ]
  },
  quality: {
    name: "Quality & Safety",
    color: "#3B82F6",
    tools: [
      {
        id: "root-cause",
        icon: Search,
        name: "Root Cause Analysis Assistant",
        description: "Conduct systematic root cause analysis for quality issues.",
        promptTemplate: "Conduct a root cause analysis for this issue: [PROBLEM DESCRIPTION]. Use the 5 Whys technique and identify potential corrective actions.",
        category: "quality"
      },
      {
        id: "safety-checklist",
        icon: Shield,
        name: "Safety Inspection Checklist Generator",
        description: "Generate comprehensive safety inspection checklists.",
        promptTemplate: "Generate a safety inspection checklist for [WORKPLACE/ACTIVITY]. Include OSHA compliance requirements and industry best practices.",
        category: "quality"
      },
      {
        id: "audit-prep",
        icon: ClipboardList,
        name: "Quality Audit Preparation",
        description: "Prepare for quality audits with documentation checklists.",
        promptTemplate: "Create an audit preparation checklist for [STANDARD/CERTIFICATION]. Include documentation requirements, common findings, and preparation steps.",
        category: "quality"
      }
    ]
  },
  industry: {
    name: "Industry & Operations",
    color: "#60A5FA",
    tools: [
      {
        id: "logistics-planner",
        icon: Truck,
        name: "Logistics Planning Assistant",
        description: "Optimize logistics and supply chain operations.",
        promptTemplate: "Help me optimize logistics for [OPERATION TYPE]. Consider route planning, inventory management, and cost efficiency.",
        category: "industry"
      },
      {
        id: "construction-scheduler",
        icon: HardHat,
        name: "Construction Schedule Assistant",
        description: "Create and optimize construction project schedules.",
        promptTemplate: "Create a construction schedule for [PROJECT TYPE]. Include phases, dependencies, resource allocation, and critical path analysis.",
        category: "industry"
      },
      {
        id: "process-optimizer",
        icon: Wrench,
        name: "Process Optimization Assistant",
        description: "Identify and implement process improvements.",
        promptTemplate: "Analyze and optimize this process: [PROCESS DESCRIPTION]. Identify bottlenecks, waste, and improvement opportunities using lean principles.",
        category: "industry"
      }
    ]
  }
};

export default function AIToolsConsole({ 
  labId,
  labCategory,
  availableTools,
  onToolSelect,
  variant = "full"
}) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [promptInput, setPromptInput] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  // Filter tools based on available tools list or show all
  const getToolsToShow = () => {
    if (availableTools && availableTools.length > 0) {
      const toolsMap = {};
      Object.entries(AI_TOOLS).forEach(([catKey, category]) => {
        const filteredTools = category.tools.filter(t => availableTools.includes(t.id));
        if (filteredTools.length > 0) {
          toolsMap[catKey] = { ...category, tools: filteredTools };
        }
      });
      return toolsMap;
    }
    return AI_TOOLS;
  };

  const toolsToShow = getToolsToShow();

  const handleCopyPrompt = (prompt, toolId) => {
    navigator.clipboard.writeText(prompt);
    setCopiedId(toolId);
    toast.success("Prompt copied to clipboard!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleUsePrompt = (tool) => {
    setSelectedTool(tool);
    setPromptInput(tool.promptTemplate);
    if (onToolSelect) {
      onToolSelect(tool);
    }
  };

  // Compact variant for sidebar
  if (variant === "compact") {
    return (
      <Card className="border-[#E5E7EB] bg-white" data-testid="ai-tools-compact">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base" style={{ color: '#991B1B' }}>
            AI Tools
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {Object.entries(toolsToShow).slice(0, 2).map(([key, category]) => (
            category.tools.slice(0, 2).map(tool => (
              <Button
                key={tool.id}
                variant="ghost"
                className="w-full justify-start gap-2 h-auto py-2 px-3 text-left"
                onClick={() => handleUsePrompt(tool)}
              >
                <tool.icon className="w-4 h-4 text-[#1E3A8A]" />
                <span className="text-sm text-[#1E3A8A] truncate">{tool.name}</span>
              </Button>
            ))
          ))}
        </CardContent>
      </Card>
    );
  }

  // Full console
  return (
    <div className="space-y-6" data-testid="ai-tools-console">
      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(toolsToShow).map(([key, category]) => (
          <Button
            key={key}
            variant={selectedCategory === key ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCategory(selectedCategory === key ? null : key)}
            className={selectedCategory === key 
              ? "text-white" 
              : "border-[#E5E7EB]"
            }
            style={selectedCategory === key ? { backgroundColor: category.color } : {}}
          >
            {category.name}
          </Button>
        ))}
      </div>

      {/* Tools Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <AnimatePresence>
          {Object.entries(toolsToShow)
            .filter(([key]) => !selectedCategory || key === selectedCategory)
            .flatMap(([key, category]) => 
              category.tools.map((tool, index) => (
                <motion.div
                  key={tool.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2, delay: index * 0.05 }}
                >
                  <AIToolCard 
                    tool={tool}
                    color={category.color}
                    onCopy={() => handleCopyPrompt(tool.promptTemplate, tool.id)}
                    onUse={() => handleUsePrompt(tool)}
                    isCopied={copiedId === tool.id}
                  />
                </motion.div>
              ))
            )}
        </AnimatePresence>
      </div>

      {/* Active Tool Panel */}
      {selectedTool && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6"
        >
          <Card className="border-[#1E3A8A]/20 bg-[#1E3A8A]/5">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#1E3A8A]" />
                <span className="font-heading text-lg text-[#1E3A8A]">
                  {selectedTool.name}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-[#1E3A8A]/70">{selectedTool.description}</p>
              <div>
                <label className="text-sm font-medium text-[#1E3A8A] mb-2 block">
                  Customize Your Prompt
                </label>
                <Textarea
                  value={promptInput}
                  onChange={(e) => setPromptInput(e.target.value)}
                  rows={4}
                  className="bg-white border-[#E5E7EB]"
                  placeholder="Edit the prompt template..."
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={() => handleCopyPrompt(promptInput, 'custom')}
                  className="bg-[#1E3A8A] text-white hover:bg-[#1E40AF]"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Prompt
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setSelectedTool(null)}
                  className="border-[#E5E7EB]"
                >
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}

// Individual tool card
function AIToolCard({ tool, color, onCopy, onUse, isCopied }) {
  return (
    <Card className="h-full border-[#E5E7EB] bg-white hover:shadow-lg transition-shadow group">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${color}15` }}
          >
            <tool.icon className="w-5 h-5" style={{ color }} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-heading text-sm font-semibold mb-1" style={{ color: '#991B1B' }}>
              {tool.name}
            </h4>
            <p className="text-xs text-[#1E3A8A]/70 line-clamp-2 mb-3">
              {tool.description}
            </p>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant="outline"
                onClick={onCopy}
                className="h-7 text-xs border-[#E5E7EB]"
              >
                {isCopied ? (
                  <><CheckCircle2 className="w-3 h-3 mr-1" /> Copied</>
                ) : (
                  <><Copy className="w-3 h-3 mr-1" /> Copy</>
                )}
              </Button>
              <Button 
                size="sm" 
                onClick={onUse}
                className="h-7 text-xs text-white"
                style={{ backgroundColor: color }}
              >
                Use
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * AIToolsSidebar - Compact list for lab page sidebars
 */
export function AIToolsSidebar({ tools = [], onSelect }) {
  const allTools = Object.values(AI_TOOLS).flatMap(cat => cat.tools);
  const filteredTools = tools.length > 0 
    ? allTools.filter(t => tools.includes(t.id))
    : allTools.slice(0, 6);

  return (
    <div className="space-y-2" data-testid="ai-tools-sidebar">
      {filteredTools.map(tool => (
        <Button
          key={tool.id}
          variant="ghost"
          className="w-full justify-start gap-2 h-auto py-2.5 px-3 text-left hover:bg-[#F8FAFC]"
          onClick={() => onSelect?.(tool)}
        >
          <tool.icon className="w-4 h-4 text-[#1E3A8A] flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm text-[#1E3A8A] font-medium truncate">{tool.name}</p>
          </div>
        </Button>
      ))}
    </div>
  );
}

// Export tools data for use in other components
export { AI_TOOLS };
